import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Layout from '../components/Layout'
import { AuthService, type AuthUser } from '../services/auth'
import { HomeworkService, type Homework, type SubmissionStats, type SchoolHomeworkSubmissionWithStudent, type SchoolStudent, type EntreClubStudent, type OverallSummary } from '../services/homework'
import { VideoService } from '../services/videos'
import type { Video, VideoWithCategory } from '../services/videos'
import { CategoryService } from '../services/categories'
import type { CategoryWithVideoCount } from '../services/categories'
import { GroupService } from '../services/groups'
import { SchoolService } from '../services/schools'
import { UserService } from '../services/users'
import type { School } from '../services/schools'
import RosterFilterBar, { type RosterFilterValue } from '../components/teacher/RosterFilterBar'
import HomeworkAttachmentList from '../components/HomeworkAttachmentList'
import { useAdminSelectedSchool } from '../utils/adminSchoolContext'
import { formatNumber } from '../utils/formatNumber'
import { compareRosterValue, normalizeClassName, normalizeGrade } from '../utils/gradeClass'
import { jstEndOfDayISO } from '../utils/jstDate'
import { scrollToElementTop } from '../utils/scrollToElement'

// ─── Constants ─────────────────────────────────────────────────────────────────

const ROSTER_PAGE_SIZE = 100

const TYPE_LABELS: Record<string, string> = {
  school: '学校',
  entre: 'アントレ部',
  system: 'システム',
}

// 宿題一覧のタブ。学校宿題は先生と管理者、アントレ部宿題は管理者のみ（RLSの都合）。
// タブはURLのクエリで表すので、リロードとブラウザの戻るでタブが保たれる。
type HomeworkKind = 'school' | 'entre'

const listPath = (kind: HomeworkKind) =>
  kind === 'entre' ? '/teacher/homework?kind=entre' : '/teacher/homework'

// 生徒の絞り込み（学年・クラス・グループ・生徒検索）はURLのクエリで持ち、宿題一覧と提出一覧で共有する。
// 検索欄は宿題一覧では宿題名（hq）、提出一覧では生徒（q）を探す。どちらも画面を行き来しても残す。
const FILTER_PARAM_KEYS = ['grade', 'class', 'groupId', 'q'] as const
const HOMEWORK_SEARCH_PARAM = 'hq'

const readRosterFilter = (params: URLSearchParams): RosterFilterValue => ({
  grade: params.get('grade'),
  class: params.get('class'),
  groupId: params.get('groupId'),
  search: params.get('q') ?? '',
})

const writeRosterFilter = (params: URLSearchParams, value: RosterFilterValue): URLSearchParams => {
  const next = new URLSearchParams(params)
  const values: Record<(typeof FILTER_PARAM_KEYS)[number], string | null> = {
    grade: value.grade, class: value.class, groupId: value.groupId, q: value.search || null,
  }
  for (const key of FILTER_PARAM_KEYS) {
    const v = values[key]
    if (v) next.set(key, v)
    else next.delete(key)
  }
  return next
}

// 遷移先のパスに今の絞り込みを引き継ぐ
const withRosterFilter = (path: string, params: URLSearchParams): string => {
  const [pathname, query = ''] = path.split('?')
  const next = new URLSearchParams(query)
  for (const key of [...FILTER_PARAM_KEYS, HOMEWORK_SEARCH_PARAM]) {
    const v = params.get(key)
    if (v) next.set(key, v)
  }
  const qs = next.toString()
  return qs ? `${pathname}?${qs}` : pathname
}

// 生徒の名前・メールアドレスの部分一致
const matchesStudentSearch = (s: { name: string; email?: string | null }, search: string): boolean => {
  const q = search.trim().toLowerCase()
  if (!q) return true
  return s.name.toLowerCase().includes(q) || (s.email?.toLowerCase().includes(q) ?? false)
}

// 名簿の行。アントレ部は複数校にまたがるので学校を持つ
type RosterStudent = SchoolStudent & { school_id?: string | null; school_name?: string | null }

const ALL_SCHOOLS = 'all'

// ─── Helpers ───────────────────────────────────────────────────────────────────

type HwStatus = '実施中' | '採点中' | '完了'

function computeStatus(hw: Homework, unreviewed: number): HwStatus {
  const now = new Date()
  const deadlinePassed = hw.deadline_at ? new Date(hw.deadline_at) < now : false
  const closed = deadlinePassed || !hw.is_active
  if (!closed) return '実施中'
  if (unreviewed > 0) return '採点中'
  return '完了'
}

function isTargetStudent(s: { grade: string | null; class: string | null }, hw: Homework): boolean {
  const grades = hw.target_grades ?? []
  const classes = hw.target_classes ?? []
  const gradeOk = grades.length === 0 || grades.includes('all') || (s.grade != null && grades.includes(s.grade))
  const classOk = classes.length === 0 || classes.includes('all') || (s.class != null && classes.includes(s.class))
  return gradeOk && classOk
}

// Numeric-aware comparison for grade/class/attendance_number text fields
function compareMaybeNumeric(a: string | null, b: string | null): number {
  if (a === b) return 0
  if (a == null) return 1
  if (b == null) return -1
  const na = Number(a)
  const nb = Number(b)
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb
  return a.localeCompare(b, 'ja')
}

// ─── Status badge ──────────────────────────────────────────────────────────────

const StatusBadge = ({ status }: { status: HwStatus }) => {
  const styles: Record<HwStatus, string> = {
    '実施中': 'bg-green-50 text-green-700',
    '採点中': 'bg-orange-50 text-orange-700',
    '完了':   'bg-gray-100 text-gray-500',
  }
  return (
    <span className={`inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full ${styles[status]}`}>
      {status}
    </span>
  )
}

// ─── Grade badge ───────────────────────────────────────────────────────────────

const GradeBadge = ({ grade }: { grade: 'A' | 'B' | 'C' | null }) => {
  if (!grade) return <span className="text-gray-400 text-sm">—</span>
  const styles = { A: 'bg-green-100 text-green-800', B: 'bg-yellow-100 text-yellow-800', C: 'bg-red-100 text-red-800' }
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${styles[grade]}`}>{grade}</span>
}

// ─── Target checkboxes ─────────────────────────────────────────────────────────

const TargetCheckboxes = ({
  options,
  selected,
  onChange,
}: {
  options: { value: string; label: string }[]
  selected: string[]
  onChange: (values: string[]) => void
}) => {
  const toggle = (value: string) => {
    if (value === 'all') {
      onChange(selected.includes('all') ? [] : ['all'])
      return
    }
    const withoutAll = selected.filter(v => v !== 'all')
    if (withoutAll.includes(value)) onChange(withoutAll.filter(v => v !== value))
    else onChange([...withoutAll, value])
  }
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const isChecked = opt.value === 'all'
          ? selected.includes('all')
          : (!selected.includes('all') && selected.includes(opt.value))
        return (
          <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={isChecked} onChange={() => toggle(opt.value)} className="rounded border-gray-300 text-[#da4574]" />
            <span className="text-sm text-gray-700">{opt.label}</span>
          </label>
        )
      })}
    </div>
  )
}

// ─── Review panel ──────────────────────────────────────────────────────────────

const ReviewPanel = ({
  submission,
  reviewerId,
  canGrade,
  onDone,
  onClose,
}: {
  submission: SchoolHomeworkSubmissionWithStudent
  reviewerId: string
  // 採点できない先生には評価とフィードバックを読み取り専用で見せる
  canGrade: boolean
  onDone: () => void
  onClose: () => void
}) => {
  const [grade, setGrade] = useState<'A' | 'B' | 'C'>(submission.grade ?? 'B')
  const [feedback, setFeedback] = useState(submission.feedback_text ?? '')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const handleConfirm = async () => {
    setIsSaving(true)
    setSaveError('')
    try {
      await HomeworkService.reviewSubmission({ submissionId: submission.id, reviewerId, grade, feedbackText: feedback, publish: false })
      onDone()
    } catch (err) {
      console.error(err)
      setSaveError('保存に失敗しました。もう一度お試しください。')
    } finally {
      setIsSaving(false)
    }
  }

  // 学年・クラスは '1年' / 'A組' の形で保存されている。数字だけの古い値にだけ単位を付ける
  const studentLabel = submission.student
    ? [
        normalizeGrade(submission.student.grade) + normalizeClassName(submission.student.class),
        submission.student.name,
      ].filter(Boolean).join(' ')
    : '生徒'

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">{studentLabel} の回答</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-5 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
            {submission.submitted_answer || '（回答なし）'}
          </div>

          <div className="mb-5">
            <HomeworkAttachmentList studentId={submission.student_id} homeworkId={submission.homework_id} />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">評価</label>
            {canGrade ? (
              <div className="flex gap-3">
                {(['A', 'B', 'C'] as const).map(g => (
                  <button
                    key={g}
                    onClick={() => setGrade(g)}
                    className={`px-6 py-2 rounded-lg font-bold text-lg transition-colors ${
                      grade === g
                        ? g === 'A' ? 'bg-green-500 text-white' : g === 'B' ? 'bg-yellow-500 text-white' : 'bg-red-500 text-white'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            ) : (
              <GradeBadge grade={submission.grade ?? null} />
            )}
          </div>

          <div className="mb-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">フィードバック</label>
            {canGrade ? (
              <textarea
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#da4574]/40"
                placeholder="生徒へのフィードバックを入力してください..."
              />
            ) : (
              <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {submission.feedback_text || '（未入力）'}
              </div>
            )}
          </div>

          {saveError && <p className="text-sm text-red-600 mb-3">{saveError}</p>}

          {!canGrade ? (
            <div className="flex justify-end">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                閉じる
              </button>
            </div>
          ) : (
          <div className="flex gap-3 justify-end">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              キャンセル
            </button>
            <button
              onClick={handleConfirm}
              disabled={isSaving || !feedback.trim()}
              className="px-6 py-2 text-sm font-semibold text-white bg-[#da4574] rounded-lg hover:bg-[#c23d67] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? '保存中...' : '保存する'}
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Submissions view ──────────────────────────────────────────────────────────

const SUMMARY_FIELDS: { key: keyof Omit<OverallSummary, 'generated_at'>; label: string }[] = [
  { key: 'summary', label: '要約' },
  { key: 'strengths', label: '良い点' },
  { key: 'challenges', label: '課題' },
  { key: 'support_policy', label: '支援方針' },
]

const SubmissionsView = ({
  homework,
  reviewerId,
  schoolId,
  canGrade,
  allSchools,
  onBack,
}: {
  homework: Homework
  reviewerId: string
  schoolId: string
  // 採点・確定ができるか（アントレ部を実施している学校の先生は閲覧のみ）
  canGrade: boolean
  // 全校横断で見るか（管理者が「管理者用の学校」を選んでいるときだけ true）
  allSchools: boolean
  onBack: () => void
}) => {
  // アントレ部宿題は全学校共通なので、名簿も提出も選択中の学校に絞る必要がある
  const isEntre = homework.type === 'entre'
  // 全校横断のときだけ学校を選び直せる。それ以外は選択中の学校に固定する。
  const canPickSchool = isEntre && allSchools
  // 学校宿題は元から1校分しか出てこない。ここで学校を足すと、管理者アカウント
  // （school_id が NULL）で試した提出が一覧から消えるので、アントレ部だけに効かせる。
  const scopeToSchool = isEntre && !allSchools
  const [submissions, setSubmissions] = useState<SchoolHomeworkSubmissionWithStudent[]>([])
  const [students, setStudents] = useState<RosterStudent[]>([])
  const [schools, setSchools] = useState<School[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [reviewing, setReviewing] = useState<SchoolHomeworkSubmissionWithStudent | null>(null)
  // アントレ部は学校切替で選んだ学校を初期値にし、ここで他校や全校に広げられる
  const [filterSchoolId, setFilterSchoolId] = useState<string | null>(isEntre ? schoolId : null)
  // 学年・クラス・グループ・検索は宿題一覧から引き継ぐ（URLのクエリ）
  const [searchParams, setSearchParams] = useSearchParams()
  const rosterFilter = useMemo(() => readRosterFilter(searchParams), [searchParams])
  const { grade: filterGrade, class: filterClass, groupId: filterGroupId, search: filterSearch } = rosterFilter
  const handleRosterFilterChange = useCallback((value: RosterFilterValue) => {
    setSearchParams(prev => writeRosterFilter(prev, value), { replace: true })
  }, [setSearchParams])
  const [groupMemberIds, setGroupMemberIds] = useState<Set<string>>(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const rosterRef = useRef<HTMLDivElement>(null)
  const [overallSummary, setOverallSummary] = useState<OverallSummary | null>(homework.overall_summary)
  const [isGenerating, setIsGenerating] = useState(false)
  const [summaryError, setSummaryError] = useState('')
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [confirmDone, setConfirmDone] = useState(false)
  const [confirmError, setConfirmError] = useState('')

  const load = useCallback(async () => {
    setIsLoading(true)
    try {
      // 全校横断のときだけ宿題の対象学校すべてを読み、画面の「学校」で絞る。
      // それ以外は選択中の学校だけをサーバー側で取り、他校の生徒情報は手元に来ない。
      const targetSchoolIds = homework.target_school_ids?.length ? homework.target_school_ids : null
      const rosterSchoolIds = canPickSchool ? targetSchoolIds : [schoolId]
      const [subs, studs, schoolList] = await Promise.all([
        HomeworkService.getSchoolHomeworkSubmissions(homework.id, scopeToSchool ? { schoolId } : undefined),
        // アントレ部の対象生徒＝アントレ部グループのメンバー（生徒側の表示条件と同じ）
        isEntre ? HomeworkService.getEntreClubStudents(rosterSchoolIds) : HomeworkService.getSchoolStudents(schoolId),
        canPickSchool ? SchoolService.getSchools() : Promise.resolve([] as School[]),
      ])
      setSubmissions(subs)
      setStudents(studs as RosterStudent[])
      setSchools(targetSchoolIds ? schoolList.filter(sc => targetSchoolIds.includes(sc.id)) : schoolList)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }, [homework.id, homework.target_school_ids, schoolId, isEntre, canPickSchool, scopeToSchool])

  useEffect(() => { load() }, [load])

  // 画面上部の学校切替に追従する（ヘッダーと表示が食い違わないように）
  useEffect(() => { if (isEntre) setFilterSchoolId(schoolId) }, [isEntre, schoolId])

  // Load group member IDs when a group filter is selected
  useEffect(() => {
    if (!filterGroupId) {
      setGroupMemberIds(new Set())
      return
    }
    let cancelled = false
    GroupService.getGroupWithMembers(filterGroupId)
      .then(group => {
        if (!cancelled) setGroupMemberIds(new Set((group?.members ?? []).map(m => m.student_id)))
      })
      .catch(err => {
        console.error(err)
        if (!cancelled) setGroupMemberIds(new Set())
      })
    return () => { cancelled = true }
  }, [filterGroupId])

  // Reset to first page whenever the filter or the underlying data changes
  useEffect(() => { setCurrentPage(1) }, [filterSchoolId, filterGrade, filterClass, filterGroupId, filterSearch, students])

  const schoolNameById = new Map(schools.map(sc => [sc.id, sc.name]))
  const schoolNameOf = (id: string | null | undefined) => (id ? schoolNameById.get(id) ?? null : null)

  // 学校の絞り込みは名簿・提出の両方に効く（集計・CSV・確定の対象も表示中の学校だけになる）
  const roster = students
    .filter(s => isTargetStudent(s, homework))
    .filter(s => !filterSchoolId || s.school_id === filterSchoolId)
    .sort((a, b) =>
      (filterSchoolId ? 0 : (schoolNameOf(a.school_id) ?? '').localeCompare(schoolNameOf(b.school_id) ?? '', 'ja')) ||
      compareMaybeNumeric(a.grade, b.grade) ||
      compareMaybeNumeric(a.class, b.class) ||
      compareMaybeNumeric(a.attendance_number, b.attendance_number)
    )
  const scopedSubmissions = filterSchoolId
    ? submissions.filter(s => s.student?.school_id === filterSchoolId)
    : submissions
  const subByStudent = new Map(scopedSubmissions.map(s => [s.student_id, s]))
  const rosterStudentIds = new Set(roster.map(s => s.id))
  const extraSubmissions = scopedSubmissions.filter(s => !rosterStudentIds.has(s.student_id))

  // Apply grade/class/group filter and the name/email search to the roster
  const filterActive = !!filterGrade || !!filterClass || !!filterGroupId || !!filterSearch.trim()
  const visibleRoster = roster.filter(s => {
    if (!matchesStudentSearch(s, filterSearch)) return false
    if (filterGroupId) return groupMemberIds.has(s.id)
    if (filterGrade && s.grade !== filterGrade) return false
    if (filterClass && s.class !== filterClass) return false
    return true
  })

  // Combined, paginated display rows. Extra submissions (from students not in the
  // target roster) are only shown when no filter is active.
  type RosterRow =
    | { kind: 'roster'; student: RosterStudent }
    | { kind: 'extra'; sub: SchoolHomeworkSubmissionWithStudent }
  const displayRows: RosterRow[] = [
    ...visibleRoster.map(s => ({ kind: 'roster' as const, student: s })),
    ...(filterActive ? [] : extraSubmissions.map(sub => ({ kind: 'extra' as const, sub }))),
  ]
  const totalPages = Math.max(1, Math.ceil(displayRows.length / ROSTER_PAGE_SIZE))
  const pageStart = (currentPage - 1) * ROSTER_PAGE_SIZE
  const pageRows = displayRows.slice(pageStart, pageStart + ROSTER_PAGE_SIZE)

  // ページ切り替え後は一覧の先頭まで戻す（下部のボタンから押しても位置が迷子にならないように）
  const changePage = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return
    setCurrentPage(page)
    requestAnimationFrame(() => scrollToElementTop(rosterRef.current))
  }

  const paginationButtons = (
    <div className="flex gap-2">
      <button
        onClick={() => changePage(currentPage - 1)}
        disabled={currentPage <= 1}
        className={`px-3 py-1 text-sm rounded transition-colors ${currentPage <= 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
      >
        ← 前へ
      </button>
      <button
        onClick={() => changePage(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className={`px-3 py-1 text-sm rounded transition-colors ${currentPage >= totalPages ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-[#da4574] text-white hover:bg-[#c23d67]'}`}
      >
        次へ →
      </button>
    </div>
  )

  const handleSchoolChange = (value: string) => {
    setFilterSchoolId(value === ALL_SCHOOLS ? null : value)
    // 学年・クラス・グループの選択肢は学校ごとなのでリセットする（検索は残す）
    handleRosterFilterChange({ ...rosterFilter, grade: null, class: null, groupId: null })
  }
  const gradeDist = { A: 0, B: 0, C: 0 }
  for (const sub of scopedSubmissions) {
    if (sub.grade) gradeDist[sub.grade] += 1
  }
  const unreviewedCount = scopedSubmissions.filter(s => !s.teacher_reviewed).length
  const status = computeStatus(homework, unreviewedCount)

  const handleDownloadCSV = () => {
    const rows = roster.map(s => {
      const sub = subByStudent.get(s.id)
      return {
        ...(canPickSchool ? { school: schoolNameOf(s.school_id) ?? '-' } : {}),
        grade: s.grade ?? '-',
        class: s.class ?? '-',
        number: s.attendance_number ?? '-',
        name: s.name,
        status: sub ? '提出済み' : '未提出',
        evaluation: sub?.grade ?? '-',
        feedback: sub?.feedback_text ?? '-',
      }
    })
    HomeworkService.exportSubmissionsCSV(rows, homework.title)
  }

  const handleGenerateSummary = async () => {
    setIsGenerating(true)
    setSummaryError('')
    try {
      const summary = await HomeworkService.generateOverallSummary(homework.id)
      setOverallSummary(summary)
    } catch (err) {
      console.error(err)
      setSummaryError(err instanceof Error ? err.message : '総評の生成に失敗しました')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleConfirmAll = async () => {
    setIsConfirming(true)
    setConfirmError('')
    try {
      // アントレ部は表示中の学校の提出だけを公開する
      await HomeworkService.confirmAllSubmissions(homework.id, isEntre ? scopedSubmissions.map(s => s.id) : undefined)
      setShowConfirmDialog(false)
      setConfirmDone(true)
      await load()
      // 確定したら総評も作り直して表示する（確定自体は済んでいるので、失敗しても総評欄にエラーを出すだけ）
      if (!isEntre && submissions.length > 0) void handleGenerateSummary()
    } catch (err) {
      console.error(err)
      setConfirmError('確定に失敗しました。もう一度お試しください。')
    } finally {
      setIsConfirming(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#da4574]" />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            戻る
          </button>
          <h2 className="text-xl font-bold text-gray-900">{homework.title} — 提出一覧</h2>
        </div>
        {isEntre && (
          <p className="text-sm text-gray-500 mt-2">
            {canPickSchool
              ? 'アントレ部宿題は全学校共通です。下の「学校」で絞り込めます（集計・CSV・「確定する」も表示中の学校が対象）。'
              : canGrade
                ? 'アントレ部宿題は全学校共通ですが、この画面では選択中の学校の生徒だけを表示・採点します。'
                : 'アントレ部宿題は全学校共通です。自校のアントレ部メンバーの提出のみ表示しています（採点は管理者が行います）。'}
          </p>
        )}
      </div>

      {/* School filter (全校横断のときだけ) */}
      {canPickSchool && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">学校</label>
          <select
            value={filterSchoolId ?? ALL_SCHOOLS}
            onChange={e => handleSchoolChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#da4574]/40 bg-white text-sm"
          >
            <option value={ALL_SCHOOLS}>全ての学校</option>
            {schools.map(sc => (
              <option key={sc.id} value={sc.id}>{sc.name}</option>
            ))}
          </select>
          <span className="text-xs text-gray-500">
            {filterSchoolId ? '' : '全ての対象校のアントレ部メンバーを表示しています'}
          </span>
        </div>
      )}

      {/* CSV download */}
      <div className="flex justify-end mb-3">
        <button
          onClick={handleDownloadCSV}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          CSVをダウンロード
        </button>
      </div>

      {/* Homework summary */}
      <div className="bg-white rounded-xl shadow overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-24">ステータス</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-20">区分</th>
              {canPickSchool && <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-32">学校</th>}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">宿題</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-28">提出期限</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-24">回答者数</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-28">未回答者数</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-32">成績分布</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-4 py-3 text-center"><StatusBadge status={status} /></td>
              <td className="px-4 py-3 text-center text-xs text-gray-600">{TYPE_LABELS[homework.type] ?? homework.type}</td>
              {canPickSchool && (
                <td className="px-4 py-3 text-center text-xs text-gray-600">
                  {filterSchoolId ? schoolNameOf(filterSchoolId) ?? '—' : '全ての学校'}
                </td>
              )}
              <td className="px-4 py-3 font-semibold text-gray-900">{homework.title}</td>
              <td className="px-4 py-3 text-center text-gray-600">
                {homework.deadline_at
                  ? new Date(homework.deadline_at).toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' })
                  : '—'}
              </td>
              <td className="px-4 py-3 text-center text-gray-800 font-medium">{formatNumber(scopedSubmissions.length)}名</td>
              <td className="px-4 py-3 text-center text-gray-800 font-medium">{formatNumber(Math.max(0, roster.length - scopedSubmissions.length))}名</td>
              <td className="px-4 py-3 text-center text-xs text-gray-700 whitespace-nowrap">
                A:{formatNumber(gradeDist.A)}名 B:{formatNumber(gradeDist.B)}名 C:{formatNumber(gradeDist.C)}名
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Overall summary (総評) — アントレ部宿題では出さない。overall_summary は宿題ごとに
          1カラムしかなく全学校の回答から生成されるため、学校別の総評を保存できない。 */}
      {!isEntre && (
      <div className="bg-white rounded-xl shadow p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-gray-900">総評</h3>
          <button
            onClick={handleGenerateSummary}
            disabled={isGenerating || submissions.length === 0}
            className="px-4 py-1.5 text-xs font-semibold text-white bg-[#da4574] rounded-lg hover:bg-[#c23d67] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isGenerating ? '生成中...' : overallSummary ? '再生成する' : 'AIで生成する'}
          </button>
        </div>
        {summaryError && <p className="text-sm text-red-600 mb-3">{summaryError}</p>}
        {overallSummary ? (
          <dl className="space-y-2">
            {SUMMARY_FIELDS.map(({ key, label }) => (
              <div key={key} className="flex gap-3 text-sm">
                <dt className="w-20 shrink-0 font-semibold text-gray-700">{label}：</dt>
                <dd className="text-gray-700 whitespace-pre-wrap leading-relaxed">{overallSummary[key]}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm text-gray-400">
            {isGenerating
              ? '総評を生成しています...'
              : submissions.length === 0
              ? '回答が提出されると総評を生成できます。'
              : '「確定する」または「AIで生成する」を押すと、提出された回答からクラス全体の総評（要約・良い点・課題・支援方針）を作成します。'}
          </p>
        )}
      </div>
      )}

      {/* Roster filters — 宿題一覧と同じ絞り込みバー。グループ・学年・クラスは学校単位なので、
          アントレ部で全校表示のときは検索だけにする */}
      {roster.length > 0 && (
        <div className="bg-white rounded-xl shadow p-4 mb-4">
          <RosterFilterBar
            schoolId={(isEntre ? filterSchoolId : schoolId) ?? schoolId}
            value={rosterFilter}
            onChange={handleRosterFilterChange}
            showRosterFilters={!!(isEntre ? filterSchoolId : schoolId)}
          />
        </div>
      )}

      {/* Student roster */}
      {roster.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow text-gray-500">
          {isEntre
            ? filterSchoolId
              ? `${schoolNameOf(filterSchoolId) ?? 'この学校'}にアントレ部のメンバーが登録されていません（生徒一覧のグループ管理で「アントレ部」グループを作成してください）`
              : '対象校にアントレ部のメンバーが登録されていません（生徒一覧のグループ管理で「アントレ部」グループを作成してください）'
            : '対象の生徒がいません'}
        </div>
      ) : displayRows.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow text-gray-500">条件に一致する生徒がいません</div>
      ) : (
        <div ref={rosterRef} className="bg-white rounded-xl shadow overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600">
            <span>
              全{formatNumber(displayRows.length)}名中 {formatNumber(pageStart + 1)}〜{formatNumber(Math.min(pageStart + ROSTER_PAGE_SIZE, displayRows.length))}名を表示
            </span>
            <div className="flex items-center gap-3">
              <span>ページ {currentPage} / {totalPages}</span>
              {totalPages > 1 && paginationButtons}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {canPickSchool && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 w-32">学校</th>}
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-16">学年</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-16">クラス</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-16">番号</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">名前</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-24">提出状況</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-16">評価</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">フィードバック</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pageRows.map(row => {
                if (row.kind === 'extra') {
                  const sub = row.sub
                  return (
                    <tr
                      key={sub.id}
                      onClick={() => setReviewing(sub)}
                      className="cursor-pointer hover:bg-gray-50 bg-blue-50/30"
                    >
                      {canPickSchool && (
                        <td className="px-4 py-3 text-gray-600 text-xs">{schoolNameOf(sub.student?.school_id) ?? '—'}</td>
                      )}
                      <td className="px-4 py-3 text-center text-gray-600">—</td>
                      <td className="px-4 py-3 text-center text-gray-600">—</td>
                      <td className="px-4 py-3 text-center text-gray-600">—</td>
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{sub.student?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs text-green-700">提出済み</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <GradeBadge grade={sub.grade ?? null} />
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <p className="whitespace-pre-wrap break-words">
                          {sub.feedback_text ?? '—'}
                        </p>
                      </td>
                    </tr>
                  )
                }
                const s = row.student
                const sub = subByStudent.get(s.id)
                return (
                  <tr
                    key={s.id}
                    onClick={() => sub && setReviewing(sub)}
                    className={sub ? 'cursor-pointer hover:bg-gray-50' : ''}
                  >
                    {canPickSchool && (
                      <td className="px-4 py-3 text-gray-600 text-xs">{schoolNameOf(s.school_id) ?? '—'}</td>
                    )}
                    <td className="px-4 py-3 text-center text-gray-600">{s.grade ? `${s.grade}` : '—'}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{s.class ? `${s.class}` : '—'}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{s.attendance_number ?? '—'}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{s.name}</td>
                    <td className="px-4 py-3 text-center">
                      {sub ? (
                        <span className="text-xs text-green-700">提出済み</span>
                      ) : (
                        <span className="text-xs text-red-600 font-medium">未提出</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <GradeBadge grade={sub?.grade ?? null} />
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <p className="whitespace-pre-wrap break-words">
                        {sub?.feedback_text ?? '—'}
                      </p>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
              <span className="text-sm text-gray-700">ページ {currentPage} / {totalPages}</span>
              {paginationButtons}
            </div>
          )}
        </div>
      )}

      {/* Bulk confirm（採点できる人だけ） */}
      {canGrade ? (
        <div className="flex flex-col items-center mt-8 gap-3">
          {confirmDone && (
            <p className="text-sm font-semibold text-green-700 bg-green-50 px-4 py-2 rounded-lg">評価が確定しました。</p>
          )}
          <p className="text-sm text-gray-600">生徒全員の評価、フィードバックが確定したら「確定する」ボタンを押してください。</p>
          <button
            onClick={() => { setConfirmDone(false); setShowConfirmDialog(true) }}
            disabled={scopedSubmissions.length === 0}
            className="px-8 py-2.5 text-sm font-semibold text-white bg-[#da4574] rounded-lg hover:bg-[#c23d67] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            確定する
          </button>
          <p className="text-sm text-gray-600">※「確定する」を押すと、提出期限後に評価・フィードバックが生徒へ表示されます。提出期限前には表示されません。</p>
        </div>
      ) : (
        <p className="text-sm text-gray-600 text-center mt-8">
          ※アントレ部宿題の採点・確定は管理者が行います。この画面は閲覧のみです。
        </p>
      )}

      {/* Confirmation dialog */}
      {canGrade && showConfirmDialog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">確認画面</h3>
            <p className="text-sm text-gray-600 mb-6">
              評価、フィードバックを確定させてよろしいでしょうか？<br />
              確定後、生徒へフィードバックが届きます。
              {isEntre && (
                <>
                  <br />
                  対象：{!filterSchoolId ? '全ての学校' : schoolNameOf(filterSchoolId) ?? '選択中の学校'}の
                  {formatNumber(scopedSubmissions.length)}件
                </>
              )}
            </p>
            {confirmError && <p className="text-sm text-red-600 mb-3">{confirmError}</p>}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmDialog(false)}
                disabled={isConfirming}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                戻る
              </button>
              <button
                onClick={handleConfirmAll}
                disabled={isConfirming}
                className="px-6 py-2 text-sm font-semibold text-white bg-[#da4574] rounded-lg hover:bg-[#c23d67] disabled:opacity-50"
              >
                {isConfirming ? '確定中...' : '確定する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {reviewing && (
        <ReviewPanel
          submission={reviewing}
          reviewerId={reviewerId}
          canGrade={canGrade}
          onDone={() => { setReviewing(null); load() }}
          onClose={() => setReviewing(null)}
        />
      )}
    </div>
  )
}

// ─── Homework form ──────────────────────────────────────────────────────────────

interface HomeworkFormData {
  title: string
  description: string
  deadline_at: string
  start_at: string
  target_grades: string[]
  target_classes: string[]
  related_video_id: string
  is_active: boolean
}

const defaultForm = (): HomeworkFormData => ({
  title: '',
  description: '',
  deadline_at: '',
  start_at: '',
  // 初期状態は「全学年・全クラス」を選択済み（お知らせと同様）
  target_grades: ['all'],
  target_classes: ['all'],
  related_video_id: '',
  is_active: true,
})

// 対象学年／クラスのチェックボックス。値は生徒の users.grade / class と同じ表記（'1年', 'A', '建築学分野' など）。
// 保存済みの値が今の生徒にない場合（卒業した学年など）も外せるよう選択肢に残す
const toTargetOptions = (schoolValues: string[], saved: string[]) => [
  { value: 'all', label: '全て' },
  ...Array.from(new Set([...schoolValues, ...saved.filter(v => v !== 'all')]))
    .sort(compareRosterValue)
    .map(value => ({ value, label: value })),
]

const homeworkToForm = (hw: Homework): HomeworkFormData => ({
  title: hw.title,
  description: hw.description ?? '',
  deadline_at: hw.deadline_at ? hw.deadline_at.slice(0, 10) : '',
  start_at: hw.start_at ? hw.start_at.slice(0, 10) : '',
  target_grades: hw.target_grades ?? [],
  target_classes: hw.target_classes ?? [],
  related_video_id: hw.related_video_id ?? '',
  is_active: hw.is_active,
})

const HomeworkFormModal = ({
  editing,
  schoolId,
  onSaved,
  onClose,
}: {
  editing: Homework | null
  schoolId: string
  onSaved: () => void
  onClose: () => void
}) => {
  const [form, setForm] = useState<HomeworkFormData>(editing ? homeworkToForm(editing) : defaultForm())
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const [categories, setCategories] = useState<CategoryWithVideoCount[]>([])
  const [allVideos, setAllVideos] = useState<VideoWithCategory[]>([])
  const [schoolVideos, setSchoolVideos] = useState<Video[]>([])
  const [selectedCategory, setSelectedCategory] = useState('')
  const [schoolGrades, setSchoolGrades] = useState<string[]>([])
  const [schoolClasses, setSchoolClasses] = useState<string[]>([])

  const SCHOOL_CAT = '__school__'

  useEffect(() => {
    UserService.getSchoolGradeClassOptions(schoolId)
      .then(({ grades, classes }) => {
        setSchoolGrades(grades)
        setSchoolClasses(classes)
      })
      .catch(err => console.error('Failed to load grade/class options:', err))
  }, [schoolId])

  useEffect(() => {
    Promise.all([
      CategoryService.getCategories(),
      VideoService.getVideos(),
      VideoService.getSchoolVideos(schoolId),
    ]).then(([cats, vids, svids]) => {
      setCategories(cats)
      setAllVideos(vids)
      setSchoolVideos(svids)
      if (editing?.related_video_id) {
        if (svids.some(v => v.id === editing.related_video_id)) {
          setSelectedCategory(SCHOOL_CAT)
        } else {
          const found = vids.find(v => v.id === editing.related_video_id)
          if (found?.category_id) setSelectedCategory(found.category_id)
        }
      }
    })
  }, [schoolId]) // eslint-disable-line react-hooks/exhaustive-deps

  const videosInCategory: Array<{ id: string; title: string }> =
    selectedCategory === SCHOOL_CAT
      ? schoolVideos
      : allVideos.filter(v => v.category_id === selectedCategory)

  const set = <K extends keyof HomeworkFormData>(key: K, value: HomeworkFormData[K]) =>
    setForm(f => ({ ...f, [key]: value }))

  const handleSave = async () => {
    if (!form.title.trim()) {
      setSaveError('タイトルは必須です')
      return
    }
    setIsSaving(true)
    setSaveError('')
    try {
      const params = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        scoring_prompt: '',
        initial_message: null,
        deadline_at: form.deadline_at ? jstEndOfDayISO(form.deadline_at) : null,
        start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
        target_grades: form.target_grades,
        target_classes: form.target_classes,
        related_video_id: form.related_video_id || null,
      }
      if (editing) {
        await HomeworkService.updateSchoolHomework(editing.id, { ...params, is_active: form.is_active })
      } else {
        await HomeworkService.createSchoolHomework({ schoolId, ...params })
      }
      onSaved()
    } catch (err) {
      console.error(err)
      setSaveError('保存に失敗しました。もう一度お試しください。')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900">{editing ? '宿題を編集' : '新しい学校宿題を作成'}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">タイトル <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.title}
                onChange={e => set('title', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#da4574]/40"
                placeholder="例：探究から学んだことを振り返ろう！"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">説明文（任意）</label>
              <textarea
                value={form.description}
                onChange={e => set('description', e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#da4574]/40"
                placeholder={"宿題の説明、設問、文字数の目安などを入力してください\n例：①取り組んだこと ②学んだこと ③今後に生かしたいことを、400字程度でまとめてください。"}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">締切</label>
                <input
                  type="date"
                  value={form.deadline_at}
                  onChange={e => set('deadline_at', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#da4574]/40"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">開始日（任意）</label>
                <input
                  type="date"
                  value={form.start_at}
                  onChange={e => set('start_at', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#da4574]/40"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">対象学年</label>
              <TargetCheckboxes
                options={toTargetOptions(schoolGrades, editing?.target_grades ?? [])}
                selected={form.target_grades}
                onChange={v => set('target_grades', v)}
              />
              {form.target_grades.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">選択なし = 全学年が対象</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">対象クラス</label>
              <TargetCheckboxes
                options={toTargetOptions(schoolClasses, editing?.target_classes ?? [])}
                selected={form.target_classes}
                onChange={v => set('target_classes', v)}
              />
              {form.target_classes.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">選択なし = 全クラスが対象</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">関連コンテンツ（任意）</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">カテゴリ</label>
                  <select
                    value={selectedCategory}
                    onChange={e => { setSelectedCategory(e.target.value); set('related_video_id', '') }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#da4574]/40"
                  >
                    <option value="">カテゴリを選択</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                    {schoolVideos.length > 0 && (
                      <option value={SCHOOL_CAT}>学校コンテンツ</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">動画</label>
                  <select
                    value={form.related_video_id}
                    onChange={e => set('related_video_id', e.target.value)}
                    disabled={!selectedCategory}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#da4574]/40 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <option value="">選択してください</option>
                    {videosInCategory.map(v => (
                      <option key={v.id} value={v.id}>{v.title}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {editing && (
              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold text-gray-700">公開状態</label>
                <button
                  onClick={() => set('is_active', !form.is_active)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.is_active ? 'bg-[#da4574]' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-sm text-gray-500">{form.is_active ? '公開中' : '非公開'}</span>
              </div>
            )}
          </div>

          {saveError && <p className="text-sm text-red-600 mt-4">{saveError}</p>}

          <div className="flex gap-3 justify-end mt-6">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              キャンセル
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 text-sm font-semibold text-white bg-[#da4574] rounded-lg hover:bg-[#c23d67] disabled:opacity-50 transition-colors"
            >
              {isSaving ? '保存中...' : editing ? '変更を保存' : '作成'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main page ─────────────────────────────────────────────────────────────────

const TeacherSchoolHomeworkPage = () => {
  const navigate = useNavigate()
  // 提出一覧はURL（/teacher/homework/<id>）で表すので、ブラウザの戻るで一覧に戻れる
  const { homeworkId } = useParams<{ homeworkId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const adminSelectedSchool = useAdminSelectedSchool()
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  // 学校宿題とアントレ部宿題は両方読む（/teacher/homework/<id> がどちらでも解決できるように）
  const [schoolHomeworkList, setSchoolHomeworkList] = useState<Homework[]>([])
  const [entreHomeworkList, setEntreHomeworkList] = useState<Homework[]>([])
  const [listLoading, setListLoading] = useState(false)
  const [listLoaded, setListLoaded] = useState(false)
  const [schoolStats, setSchoolStats] = useState<Map<string, SubmissionStats>>(new Map())
  const [entreStats, setEntreStats] = useState<Map<string, SubmissionStats>>(new Map())
  // 一覧の絞り込みのグループのメンバー（null = グループ未選択）
  const [listGroupMemberIds, setListGroupMemberIds] = useState<Set<string> | null>(null)
  const [schoolStudents, setSchoolStudents] = useState<SchoolStudent[]>([])
  const [entreStudents, setEntreStudents] = useState<EntreClubStudent[]>([])
  // アントレ部を実施している学校（null = まだ判定できていない。判定前はタブを出さない）
  const [entreClubSchoolIds, setEntreClubSchoolIds] = useState<Set<string> | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingHomework, setEditingHomework] = useState<Homework | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await AuthService.getCurrentUser()
        if (!user) { navigate('/?login=true'); return }
        if (!['teacher', 'admin', 'super_admin'].includes(user.role)) { navigate('/videos'); return }
        setCurrentUser(user)
      } catch {
        navigate('/?login=true')
      } finally {
        setIsLoading(false)
      }
    }
    checkAuth()
  }, [navigate])

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin'
  // アントレ部の採点・確定は管理者のみ。実施校の先生は自校の提出を閲覧できる（RLSもSELECTだけ）。
  const canGradeEntre = isAdmin
  const canViewEntre = isAdmin || entreClubSchoolIds?.has(currentUser?.school_id ?? '') === true
  // 管理者用の学校（ミラッソ高校）を選んでいる間だけ全校横断。各校を選んでいる間はその学校だけ。
  const isAllSchools = isAdmin && adminSelectedSchool?.isAdminSchool === true
  const kind: HomeworkKind = canViewEntre && searchParams.get('kind') === 'entre' ? 'entre' : 'school'

  const schoolId = currentUser?.role === 'teacher'
    ? currentUser.school_id
    : adminSelectedSchool?.id ?? null

  // アントレ部の実施校＝アントレ部グループがある学校。先生はRLSで自校しか読めないので、
  // この呼び出しがそのまま「自校が実施校か」の判定になる。
  useEffect(() => {
    if (!currentUser) return
    let cancelled = false
    GroupService.getEntreClubSchoolIds()
      .then(ids => { if (!cancelled) setEntreClubSchoolIds(ids) })
      .catch(err => {
        console.error(err)
        if (!cancelled) setEntreClubSchoolIds(new Set())
      })
    return () => { cancelled = true }
  }, [currentUser])

  const loadList = useCallback(async () => {
    if (!schoolId) return
    setListLoading(true)
    try {
      const [schoolHw, entreHw] = await Promise.all([
        HomeworkService.getSchoolHomeworkList(schoolId),
        !canViewEntre
          ? Promise.resolve([] as Homework[])
          : isAllSchools
            ? HomeworkService.getEntreHomeworkList()
            : HomeworkService.getEntreHomeworkListForSchool(schoolId),
      ])
      setSchoolHomeworkList(schoolHw)
      setEntreHomeworkList(entreHw)
      const [sStats, eStats, students, entreMembers] = await Promise.all([
        HomeworkService.getSubmissionStats(schoolHw.map(hw => hw.id)),
        // アントレ部の集計は表示中の範囲（全校 or この学校）に合わせる
        HomeworkService.getSubmissionStats(entreHw.map(hw => hw.id), isAllSchools ? undefined : { schoolId }),
        HomeworkService.getSchoolStudents(schoolId),
        canViewEntre
          ? HomeworkService.getEntreClubStudents(isAllSchools ? null : [schoolId])
          : Promise.resolve([] as EntreClubStudent[]),
      ])
      setSchoolStats(sStats)
      setEntreStats(eStats)
      setSchoolStudents(students)
      setEntreStudents(entreMembers)
    } catch (err) {
      console.error(err)
    } finally {
      setListLoading(false)
      setListLoaded(true)
    }
  }, [schoolId, canViewEntre, isAllSchools])

  useEffect(() => {
    if (!isLoading && schoolId) loadList()
  }, [isLoading, schoolId, loadList])

  // 両方の一覧から探すので、?kind= の無い直リンクでも提出一覧が開ける
  const allHomework = useMemo(
    () => [...schoolHomeworkList, ...entreHomeworkList],
    [schoolHomeworkList, entreHomeworkList]
  )
  const viewingSubmissions = homeworkId
    ? allHomework.find(hw => hw.id === homeworkId) ?? null
    : null

  // 削除済みや学校切替で対象外になった宿題のURLを開いたときは一覧に戻す
  useEffect(() => {
    if (listLoaded && homeworkId && !allHomework.some(hw => hw.id === homeworkId)) {
      navigate(withRosterFilter(listPath(kind), searchParams), { replace: true })
    }
  }, [listLoaded, homeworkId, allHomework, kind, navigate, searchParams])

  const handleDelete = async (id: string) => {
    try {
      await HomeworkService.deleteSchoolHomework(id)
      setDeleteConfirm(null)
      await loadList()
    } catch (err) {
      console.error(err)
    }
  }

  const homeworkList = kind === 'entre' ? entreHomeworkList : schoolHomeworkList
  const submissionStats = kind === 'entre' ? entreStats : schoolStats
  const rosterForCounts = kind === 'entre' ? entreStudents : schoolStudents

  // 一覧の絞り込みはURLのクエリで持つ（提出一覧から戻ったり、リロードしても保たれる）
  // 一覧の検索欄は宿題名を探す（生徒検索 q は提出一覧のもので、ここでは使わずに残しておく）
  const listFilter = useMemo<RosterFilterValue>(
    () => ({ ...readRosterFilter(searchParams), search: searchParams.get(HOMEWORK_SEARCH_PARAM) ?? '' }),
    [searchParams]
  )

  const handleListFilterChange = useCallback((value: RosterFilterValue) => {
    setSearchParams(prev => {
      const next = writeRosterFilter(prev, { ...value, search: prev.get('q') ?? '' })
      if (value.search) next.set(HOMEWORK_SEARCH_PARAM, value.search)
      else next.delete(HOMEWORK_SEARCH_PARAM)
      return next
    }, { replace: true })
  }, [setSearchParams])

  useEffect(() => {
    if (!listFilter.groupId) {
      setListGroupMemberIds(null)
      return
    }
    let cancelled = false
    GroupService.getGroupWithMembers(listFilter.groupId)
      .then(group => {
        if (!cancelled) setListGroupMemberIds(new Set((group?.members ?? []).map(m => m.student_id)))
      })
      .catch(err => {
        console.error(err)
        if (!cancelled) setListGroupMemberIds(new Set())
      })
    return () => { cancelled = true }
  }, [listFilter.groupId])

  // アントレ部の全校横断は学校をまたぐので、この学校の学年・クラス・グループでは絞らない
  const showRosterFilters = !(kind === 'entre' && isAllSchools)
  const rosterFilterActive = showRosterFilters && (!!listFilter.grade || !!listFilter.class || !!listFilter.groupId)
  const filteredRoster = useMemo(() => {
    if (!rosterFilterActive) return rosterForCounts
    return rosterForCounts.filter(s => {
      if (listFilter.groupId) return listGroupMemberIds?.has(s.id) ?? false
      if (listFilter.grade && s.grade !== listFilter.grade) return false
      if (listFilter.class && s.class !== listFilter.class) return false
      return true
    })
  }, [rosterForCounts, rosterFilterActive, listFilter, listGroupMemberIds])

  // 選んだ生徒が1人でも対象に入る宿題だけを出し、回答者数・未回答者数もその生徒だけで数え直す
  const visibleHomework = useMemo(() => {
    const q = listFilter.search.trim().toLowerCase()
    return homeworkList
      .filter(hw => !q || hw.title.toLowerCase().includes(q))
      .map(hw => {
        const stats = submissionStats.get(hw.id)
        const targets = filteredRoster.filter(s => isTargetStudent(s, hw))
        if (!rosterFilterActive) {
          const submitted = stats?.submitted ?? 0
          return { hw, submitted, unanswered: Math.max(0, targets.length - submitted), hasTargets: true }
        }
        const submitted = stats ? targets.filter(s => stats.submittedStudentIds.has(s.id)).length : 0
        return { hw, submitted, unanswered: targets.length - submitted, hasTargets: targets.length > 0 }
      })
      .filter(row => row.hasTargets)
  }, [homeworkList, submissionStats, filteredRoster, rosterFilterActive, listFilter.search])

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#da4574]" />
        </div>
      </Layout>
    )
  }

  if (isAdmin && !adminSelectedSchool) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-12 text-center text-gray-500">
          <p className="text-lg mb-2">学校を選択してください</p>
          <p className="text-sm">画面上部のボタンから学校を選択すると、その学校の宿題が表示されます。</p>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {viewingSubmissions && currentUser && schoolId ? (
          <SubmissionsView
            homework={viewingSubmissions}
            reviewerId={currentUser.id}
            schoolId={schoolId}
            canGrade={viewingSubmissions.type === 'entre' ? canGradeEntre : true}
            allSchools={isAllSchools}
            onBack={() => { navigate(withRosterFilter(listPath(viewingSubmissions.type === 'entre' ? 'entre' : 'school'), searchParams)); loadList() }}
          />
        ) : (
          <>
            {/* 宿題種別タブ（Link なので中クリックとブラウザの戻るがそのまま効く） */}
            {canViewEntre && (
              <div className="flex border-b border-gray-200 mb-6">
                {([
                  { key: 'school' as const, label: '学校宿題' },
                  { key: 'entre' as const, label: 'アントレ部宿題' },
                ]).map(tab => (
                  <Link
                    key={tab.key}
                    to={withRosterFilter(listPath(tab.key), searchParams)}
                    className={`py-3 px-6 border-b-2 font-medium text-sm transition-colors ${
                      kind === tab.key
                        ? 'border-[#da4574] text-[#da4574]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </Link>
                ))}
              </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">宿題一覧</h1>
              {kind === 'school' ? (
                <button
                  onClick={() => { setEditingHomework(null); setShowForm(true) }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#da4574] text-white text-sm font-semibold rounded-lg hover:bg-[#c23d67] transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  宿題の作成
                </button>
              ) : !canGradeEntre && (
                <p className="text-sm text-gray-500">
                  アントレ部宿題は閲覧のみです（作成・採点は管理者が行います）。
                </p>
              )}
            </div>

            {/* Filters */}
            {schoolId && (
              <div className="bg-white rounded-xl shadow p-4 mb-4">
                <RosterFilterBar
                  schoolId={schoolId}
                  value={listFilter}
                  onChange={handleListFilterChange}
                  searchPlaceholder="宿題"
                  showRosterFilters={showRosterFilters}
                />
              </div>
            )}

            {/* List */}
            {listLoading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#da4574]" />
              </div>
            ) : homeworkList.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl shadow">
                {kind === 'entre' ? (
                  <>
                    <p className="text-gray-500 text-lg mb-2">まだアントレ部宿題がありません</p>
                    <p className="text-gray-400 text-sm">アントレ部宿題が出されるとここに表示されます。</p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-500 text-lg mb-2">まだ学校宿題がありません</p>
                    <p className="text-gray-400 text-sm">「＋ 宿題の作成」ボタンから作成してください。</p>
                  </>
                )}
              </div>
            ) : visibleHomework.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl shadow">
                <p className="text-gray-500">条件に合う宿題はありません</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-24">ステータス</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-20">区分</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">宿題</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-28">提出期限</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-24">回答者数</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-28">未回答者数</th>
                      {kind === 'school' && <th className="px-4 py-3 w-24" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visibleHomework.map(({ hw, submitted, unanswered }) => {
                      // ステータスは絞り込みに関係なく宿題全体の採点状況で決める
                      const status = computeStatus(hw, submissionStats.get(hw.id)?.unreviewed ?? 0)
                      return (
                        <tr key={hw.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-center">
                            <StatusBadge status={status} />
                          </td>
                          <td className="px-4 py-3 text-center text-xs text-gray-600">
                            {TYPE_LABELS[hw.type] ?? hw.type}
                          </td>
                          <td className="px-4 py-3">
                            <Link
                              to={withRosterFilter(`/teacher/homework/${hw.id}${kind === 'entre' ? '?kind=entre' : ''}`, searchParams)}
                              className="text-[#da4574] font-semibold hover:underline text-left"
                            >
                              {hw.title}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-600">
                            {hw.deadline_at
                              ? new Date(hw.deadline_at).toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' })
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-800 font-medium">
                            {formatNumber(submitted)}名
                          </td>
                          <td className="px-4 py-3 text-center text-gray-800 font-medium">
                            {formatNumber(unanswered)}名
                          </td>
                          {kind === 'school' && (
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-3">
                                <button
                                  onClick={() => { setEditingHomework(hw); setShowForm(true) }}
                                  className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                                >
                                  編集
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(hw.id)}
                                  className="text-xs text-red-400 hover:text-red-600 hover:underline"
                                >
                                  削除
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Create / edit form modal */}
        {showForm && schoolId && (
          <HomeworkFormModal
            editing={editingHomework}
            schoolId={schoolId}
            onSaved={() => { setShowForm(false); setEditingHomework(null); loadList() }}
            onClose={() => { setShowForm(false); setEditingHomework(null) }}
          />
        )}

        {/* Delete confirmation */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-bold text-gray-900 mb-2">宿題を削除しますか？</h3>
              <p className="text-sm text-gray-600 mb-6">この操作は取り消せません。提出済みのデータも削除されます。</p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  キャンセル
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600"
                >
                  削除する
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}

export default TeacherSchoolHomeworkPage
